import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, ListItem, Skeleton, SkeletonList, StatCard, PageHeader } from '@nexply/ui';
import {
  Users as UsersIcon,
  Sparkles,
  Cake,
  ArrowRight,
  Map as MapIcon,
  Smartphone,
  User,
  Search as SearchIcon,
} from 'lucide-react';
import type { MemberDTO } from '@nexply/api-client';
import { daysUntilBirthday } from '@nexply/core';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';

const BIRTHDAY_WINDOW_DAYS = 30;
const BIRTHDAY_LIMIT = 5;

interface Shortcut { to: string; label: string; icon: typeof UsersIcon; }
const SHORTCUTS: Shortcut[] = [
  { to: '/members', label: 'Mitglied', icon: UsersIcon },
  { to: '/map', label: 'Karte', icon: MapIcon },
  { to: '/matches', label: 'Vernetzer', icon: Sparkles },
  { to: '/profile', label: 'Profil', icon: User },
];

function fmtBirthday(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const days = daysUntilBirthday(d);
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long' });
  if (days === 0) return `${date} · heute`;
  if (days === 1) return `${date} · morgen`;
  return `${date} · in ${days} Tagen`;
}

export function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const firstName = user?.firstName ?? user?.name?.split(' ')[0] ?? '';

  const hour = new Date().getHours();
  const greet = hour < 5 ? 'Gute Nacht' : hour < 11 ? 'Guten Morgen' : hour < 18 ? 'Servus' : 'Guten Abend';

  // Nur die Gesamtzahl interessiert — darum die kleinstmögliche Seite abfragen.
  const membersQuery = useQuery({
    queryKey: ['members', { limit: 1 }],
    queryFn: () => api.members.list({ limit: 1 }),
  });
  const matchesQuery = useQuery({ queryKey: ['matches', { status: null }], queryFn: () => api.matches.list() });
  const birthdaysQuery = useQuery({
    queryKey: ['members', { birthdayWithinDays: BIRTHDAY_WINDOW_DAYS }],
    queryFn: () => api.members.list({ birthdayWithinDays: BIRTHDAY_WINDOW_DAYS, limit: 50 }),
  });

  const birthdays: MemberDTO[] = useMemo(() => {
    const all = (birthdaysQuery.data?.members ?? []).filter((m) => m.birthday);
    return all
      .slice()
      .sort((a, b) => daysUntilBirthday(new Date(a.birthday!)) - daysUntilBirthday(new Date(b.birthday!)))
      .slice(0, BIRTHDAY_LIMIT);
  }, [birthdaysQuery.data]);

  const next = birthdays[0];
  const later = birthdays.slice(1);
  const birthdaysLoading = birthdaysQuery.isPending;

  const statValue = (pending: boolean, v: string | number) =>
    pending ? <Skeleton className="h-8 w-24" /> : v;

  return (
    <div className="space-y-6 animate-slide-up">
      <PageHeader
        eyebrow={`Heute · ${new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}`}
        title={`${greet}${firstName ? `, ${firstName}` : ''}`}
        subtitle="Dein Netzwerk auf einen Blick."
      />

      <div className="lg:grid lg:grid-cols-[2fr_1fr] lg:gap-8 space-y-6 lg:space-y-0">
        <div className="space-y-6">
          {/* Stats - mobile horizontal scroll, md+ grid */}
          <div className="-mx-4 px-4 overflow-x-auto scrollbar-none md:mx-0 md:px-0 md:overflow-visible">
            <div className="flex gap-3 min-w-max md:min-w-0 md:grid md:grid-cols-3 md:gap-4">
              <StatCard
                className="min-w-[180px] md:min-w-0"
                accent
                label="Mitglieder"
                value={statValue(membersQuery.isPending, membersQuery.data?.total ?? '')}
                hint="In deinem Netzwerk"
              />
              <StatCard
                className="min-w-[180px] md:min-w-0"
                label="Matches"
                value={statValue(matchesQuery.isPending, matchesQuery.data?.matches.length ?? '')}
                hint="Gesamt"
              />
              <StatCard
                className="min-w-[180px] md:min-w-0"
                label="Geburtstage"
                value={statValue(birthdaysLoading, birthdaysQuery.data?.total ?? '')}
                hint={`Nächste ${BIRTHDAY_WINDOW_DAYS} Tage`}
              />
            </div>
          </div>

          <Card variant="elevated" interactive onClick={() => nav({ to: '/members' })} className="p-5 lg:p-6">
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gold/10 text-gold">
                <Cake className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider text-text-muted">Nächster Geburtstag</div>
                {birthdaysLoading ? (
                  <>
                    <Skeleton className="mt-1 h-5 w-48" />
                    <Skeleton className="mt-2 h-4 w-32" />
                  </>
                ) : next ? (
                  <>
                    <div className="mt-0.5 text-base lg:text-lg font-semibold text-text truncate">{next.name}</div>
                    <div className="text-sm text-text-dim">{fmtBirthday(next.birthday!)}</div>
                  </>
                ) : (
                  <>
                    <div className="mt-0.5 text-base lg:text-lg font-semibold text-text">Keiner in Sicht</div>
                    <div className="text-sm text-text-dim">
                      Trage Geburtstage bei deinen Mitgliedern ein — sie tauchen hier automatisch auf.
                    </div>
                  </>
                )}
              </div>
              <ArrowRight className="h-5 w-5 text-text-muted" />
            </div>
          </Card>

          {(birthdaysLoading || later.length > 0) && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">Danach</h2>
                <button onClick={() => nav({ to: '/members' })} className="text-xs text-gold-light hover:text-gold transition">
                  Mitglieder öffnen
                </button>
              </div>
              {birthdaysLoading ? (
                <SkeletonList count={3} />
              ) : (
                <div className="space-y-2">
                  {later.map((m) => (
                    <ListItem
                      key={m.id}
                      leading={<Cake className="h-5 w-5 text-gold" />}
                      title={m.name}
                      subtitle={fmtBirthday(m.birthday!)}
                      onClick={() => nav({ to: '/members' })}
                      chevron
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">Aktivität</h2>
            </div>
            <div className="space-y-2">
              <ListItem
                leading={<SearchIcon className="h-5 w-5 text-gold" />}
                title="Smart Search starten"
                subtitle="Finde Mitglieder per natürlicher Sprache"
                onClick={() => nav({ to: '/members' })}
                chevron
              />
              <ListItem
                leading={<Sparkles className="h-5 w-5 text-gold" />}
                title="Match-Vorschläge prüfen"
                subtitle="KI-basierte Empfehlungen"
                onClick={() => nav({ to: '/matches' })}
                chevron
              />
              <ListItem
                leading={<Smartphone className="h-5 w-5 text-gold" />}
                title="Telefon-Kontakte einpflegen"
                subtitle="Kontakte aus dem Handy übernehmen"
                onClick={() => nav({ to: '/members' })}
                chevron
              />
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">Schnellzugriff</h2>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-2 gap-3">
              {SHORTCUTS.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.to}
                    onClick={() => nav({ to: s.to as any })}
                    className="rounded-2xl border border-line bg-surface-2 px-3 py-5 text-center active:scale-95 hover:border-gold/20 hover:bg-surface-3/60 transition"
                  >
                    <div className="mx-auto grid h-10 w-10 place-items-center rounded-xl bg-gold/10 text-gold">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="mt-2.5 text-xs font-medium text-text">{s.label}</div>
                  </button>
                );
              })}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
